import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getCreditApplication,
  updateApplicationUpdateHistory,
  updateCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import {
  AddressVerification,
  BankReference,
  ConditionalOptionValues,
  CreditApplication,
  CreditApplicationStep,
  DynamicInvitedSignaturesFields,
  GroupedCreditApplicationFields,
  blockedBankReferenceEmailMessage,
  blockedTradeReferenceEmailMessage,
  findBlockedBankReferenceEmail,
  findBlockedTradeReferenceEmailDomain,
  getBlockedTradeReferenceEmailDomains,
  getCreditApplicationTypeLabel,
} from '@dill/dill-shared';
import { useLocation } from 'react-router-dom';
import { AppButton } from '../../../general/AppButton/AppButton';
import { getCreditApplicationTemplate } from '../../../../redux/services/creditApplicationsTemplatesService';
import { ConditionalOptionPreviewRef } from '../CreditApplicationComponents/ConditionalOptionPreview';
import DynamicStepForm from '../CreditApplicationComponents/DynamicStepForm';
import { verifyAddressFieldsOnSubmit } from '../CreditApplicationComponents/verifyAddressFieldsOnSubmit';
import CustomerSignatureModal from '../CreditApplicationComponents/CustomerSignatureModal';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import {
  checkFirebaseUser,
  createCreditAppFirebaseUser,
  firebaseConfirmLoginUser,
  firebaseSignInWithCustomToken,
  unverifyFirebaseUserAndSendLink,
} from '../../../../redux/services/authService';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';

enum ModalState {
  UPDATE_FIELDS,
  SUCCESS_UPDATING_FIELDS,
  VERIFY_LOGIN,
}
type StateInvitedUser = {
  title: string;
  email: string;
  step: number;
  signatureGroupName?: string;
  placeHolder?: string;
  type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  fields: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  linkedFieldId?: string | null;
  id?: string | null;
  signaturePageFieldIds?: string | null;
  signature?: string;
  dateSigned?: string;
  ipAddress?: string;
  location?: string;
  isRequired?: boolean;
};

export const ReviewAndUpdateCreditApplications = () => {
  const dispatch = useAppDispatch();
  const stepRefs = useRef<Record<number, ConditionalOptionPreviewRef | null>>({});
  const loginCheckInitiatedRef = useRef(false);
  const verificationFailedRef = useRef(false);
  const location = useLocation();
  const [creditApplicationId, setCreditApplicationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState(ModalState.VERIFY_LOGIN);
  const [modalDetails, setModalDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  }>({
    modalType: 'Success',
    title: '',
    details: '',
  });
  const [errors, setErrors] = useState<
    {
      uniqueId: string;
      errorText: string;
    }[]
  >([]);
  const [allStepFieldsValues, setAllStepFieldsValues] = useState<
    {
      uniqueId: string;
      id: string;
      name: string;
      fromField:
        | 'TEXT'
        | 'TEXT_AREA'
        | 'NUMBER'
        | 'CURRENCY'
        | 'DATE'
        | 'CHECK_BOX'
        | 'OPTION_FIELD'
        | 'FILE'
        | 'DOCUMENT_FIELD'
        | 'INVITE_USER'
        | 'DATE'
        | 'CONDITIONAL_OPTION'
        | 'BANK_REFERENCE'
        | 'TRADE_REFERENCE'
        | 'CONDITIONAL_SELECTED_FIELD'
        | 'STATE_DROPDOWN'
        | 'CHECKBOX_LIST'
        | 'CONDITIONAL_FORM'
        | 'DYNAMIC_FORM_DOCUMENT'
        | 'AUTOFILL_CHECKBOX'
        | 'INTERNAL_SIGNER'
        | 'DROPDOWN';
      isRequired: boolean;
      isFieldEmail: boolean;
      isHashed: boolean;
      stepName: string;
      stepNumber: number;
      index: number;
      groupStepPosition: number;
      groupName: string;
      groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      isFieldArray: boolean;
      groupedPosition: number;
      value: string | number | boolean | File | any | null;
      optionType?: string;
      secondaryText?: string;
      status?:
        | 'Pending'
        | 'Requested'
        | 'Submitted'
        | 'Rejected'
        | 'Blocked'
        | 'Dropped'
        | 'Delivered'
        | 'Deferred'
        | 'Bounce';
      emailDate?: string;
      sendEmail?: boolean;
      type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      secondaryValue?: string | null;
      fieldValue?: ConditionalOptionValues[];
      onlyShowPlaidOption?: boolean;
      showIfNotPlaid?: boolean;
      linkedFieldId?: string | null;
      populateFieldSignatureId?: string | null;
      currencyDecimalPlaces?: number;
      addressVerification?: AddressVerification | null;
      borderId?: string | null;
      formTemplate?: string | null;
      conditionalVisibility?: any;
      signaturePageFieldIds?: string | null;
    }[]
  >([]);
  const [personalDetailsValues, setPersonalDetailsValues] = useState<
    {
      uniqueId: string;
      id: string;
      name: string;
      fromField:
        | 'TEXT'
        | 'TEXT_AREA'
        | 'NUMBER'
        | 'CURRENCY'
        | 'DATE'
        | 'CHECK_BOX'
        | 'OPTION_FIELD'
        | 'FILE'
        | 'DOCUMENT_FIELD'
        | 'INVITE_USER'
        | 'DATE'
        | 'CONDITIONAL_OPTION'
        | 'BANK_REFERENCE'
        | 'TRADE_REFERENCE'
        | 'CONDITIONAL_SELECTED_FIELD'
        | 'STATE_DROPDOWN'
        | 'CHECKBOX_LIST'
        | 'FORM'
        | 'CONDITIONAL_FORM'
        | 'DYNAMIC_FORM_DOCUMENT'
        | 'AUTOFILL_CHECKBOX'
        | 'INTERNAL_SIGNER'
        | 'DROPDOWN';
      isRequired: boolean;
      isFieldEmail: boolean;
      isHashed: boolean;
      stepName: string;
      stepNumber: number;
      index: number;
      groupStepPosition: number;
      groupName: string;
      groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      isFieldArray: boolean;
      groupedPosition: number;
      value: string | number | boolean | File | any | null;
      optionType?: string;
      secondaryText?: string;
      status?: 'Pending' | 'Requested' | 'Submitted' | 'Rejected';
      emailDate?: string;
      sendEmail?: boolean;
      type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      secondaryValue?: string | null;
      fieldValue?: ConditionalOptionValues[];
    }[]
  >([]);
  const [fetchedCreditApplicationTemplate, setFetchedCreditApplicationTemplate] = useState<
    CreditApplicationStep[]
  >([]);
  const [decryptedCreditApplication, setDecryptedCreditApplication] = useState<
    (CreditApplication & { applicationHistoryId: string | null }) | null
  >(null);
  const [userFirstName, setUserFirstName] = useState<string>('');
  const [userLastName, setUserLastName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [updateType, setUpdateType] = useState<string>('');

  const { basicSupplier } = useAppSelector((state) => state.buyers);
  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { creditApplicationTemplate } = useAppSelector((state) => state.creditApplicationTemplates);
  const { decryptValue } = useDecryption();
  const [bankReference, setBankReference] = useState<BankReference[] | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [domainIdUrl, setDomainIdUrl] = useState<string | null>(null);
  const [domainDetails, setDomainDetails] = useState<{
    id: string;
    domainId: string;
    url: string;
    name: string;
    branches: { name: string; erpId?: string }[];
    salesReps: { name: string; erpId?: string; isHouseRep?: boolean }[];
  } | null>(null);
  const [applicationHistoryId, setApplicationHistoryId] = useState<string | null>(null);
  const [initialFormData, setInitialFormData] = useState<
    { name: string; value: any; fieldValue?: unknown }[] | null
  >(null);
  const [invitedSignatures, setInvitedSignatures] = useState<StateInvitedUser[]>([]);
  const [invitedSignaturesErrors, setInvitedSignaturesErrors] = useState<
    { placeHolder: string; errorText: string }[]
  >([]);
  const [openSignNowModal, setOpenSignNowModal] = useState(false);
  const [signNowFieldData, setSignNowFieldData] = useState<{
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  } | null>(null);
  const [signedNowPlaceholders, setSignedNowPlaceholders] = useState<string[]>([]);
  const [signature, setSignature] = useState('');
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [ipdData, setIpData] = useState<{ ipAddress: string; location: string } | null>(null);

  useEffect(() => {
    async function fetchIpData() {
      try {
        const ipRes = await fetch('https://free.freeipapi.com/api/json/');
        const ipData = await ipRes.json();
        setIpData({
          ipAddress: ipData.ipAddress,
          location: `${ipData.cityName}, ${ipData.regionName}, ${ipData.countryName}`,
        });
      } catch (err) {
        console.error(err);
      }
    }
    fetchIpData();
  }, []);

  const fetchData = async (id: string) => {
    setCreditApplicationId(id);
    await dispatch(getCreditApplication({ creditApplicationId: id, hashedType: 'encrypted' }));
  };

  const fetchDataUsingDomainId = async (domainId: string) => {
    await dispatch(getBasicSupplier({ supplierDomainId: domainId }));
  };

  const fetchDataUsingTemplateId = async (id: string) => {
    await dispatch(getCreditApplicationTemplate({ templateId: id }));
  };

  const getDefaultValue = (f: any) => {
    if (['TEXT', 'TEXT_AREA', 'OPTION_FIELD', 'NUMBER', 'CURRENCY'].includes(f.fromField))
      return '';
    if (f.fromField === 'CHECK_BOX') return false;
    return null;
  };

  const buildUniqueId = (
    stName: string,
    groupName: string,
    position: number,
    columnType: string,
    groupedPosition: number,
    id: string
  ) =>
    `${stName} ${groupName} ${position} ${columnType} ${groupedPosition} ${id || ''}`
      .toLowerCase()
      .replace(/\s+/g, '-');

  const applyFieldData = ({ st, field, f, groupedPosition = 0, index = 0 }: any) => {
    const uniqueId = buildUniqueId(
      st.name,
      field.groupName,
      field.position,
      field.columnType,
      groupedPosition,
      f.id
    );

    const foundField =
      decryptedCreditApplication?.fieldValues?.find((stFd) => stFd.uniqueId === uniqueId) || null;

    const fieldValue = foundField?.fieldValue ?? null;

    const value = foundField?.value ?? getDefaultValue(f);

    return {
      uniqueId,
      id: f.id || '',
      name: f.name || '',
      fromField: f.fromField || '',
      isRequired: f.isRequired || false,
      isHashed: f.isHashed || false,
      stepName: st.name,
      stepNumber: st.position || 0,
      groupStepPosition: field.position || 0,
      groupedPosition,
      groupName: field.groupName || '',
      groupColumnType: field.columnType || 'ONE_COLUMN',
      isFieldArray: field.isFieldArray || false,
      isFieldEmail: f.isFieldEmail || false,
      index,
      value,
      ...(f.optionType && { optionType: f.optionType }),
      ...(f.secondaryText && { secondaryText: f.secondaryText }),
      ...(f.status && { status: f.status }),
      ...(f.emailDate && { emailDate: f.emailDate }),
      ...(field.sendEmail && { sendEmail: field.sendEmail }),
      ...(f.type && { type: f.type }),
      ...(fieldValue && { fieldValue }),
      ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
      ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
      ...(f.conditionalVisibility ? { conditionalVisibility: f.conditionalVisibility } : {}),
    };
  };

  const buildFormSnapshot = (
    stepFields: typeof allStepFieldsValues,
    bankRef?: typeof bankReference,
    sigRef?: typeof invitedSignatures
  ) => {
    const stepSnapshot = stepFields.map((val) => ({
      name: val.id,
      value: val.value,
      fieldValue: (val as { fieldValue?: unknown }).fieldValue ?? null,
    }));

    const bankSnapshot = (bankRef ?? []).map((bank) => ({
      name: `${bank.bankDetails.bankName}`,
      value: bank.bankDetails.accountNumber,
    }));

    const sigSnapshot = (sigRef ?? []).map((sig) => ({
      name: sig.placeHolder ?? '',
      value: sig.email,
    }));

    return [...stepSnapshot, ...bankSnapshot, ...sigSnapshot].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  };

  useEffect(() => {
    if (initialFormData !== null) return;
    if (allStepFieldsValues.length === 0) return;

    setInitialFormData(
      structuredClone(buildFormSnapshot(allStepFieldsValues, bankReference, invitedSignatures))
    );
  }, [allStepFieldsValues, bankReference, initialFormData, invitedSignatures]);

  useEffect(() => {
    let isMounted = true;

    const decrypt = async () => {
      if (!creditApplication) {
        if (isMounted) setDecryptedCreditApplication(null);
        return;
      }

      const historyRecord = applicationHistoryId
        ? creditApplication.applicationUpdateHistory?.find(
            (h: any) => h.id === applicationHistoryId
          )
        : null;

      const creditApplicationCopy = historyRecord
        ? {
            ...historyRecord,
            id: creditApplication.id,
            applicationHistoryId: historyRecord.id,
          }
        : creditApplication.applicationUpdateHistory &&
          creditApplication.applicationUpdateHistory.length > 0
        ? {
            ...creditApplication.applicationUpdateHistory[0],
            id: creditApplication.id,
            applicationHistoryId: creditApplication.applicationUpdateHistory[0].id,
          }
        : { ...creditApplication, applicationHistoryId: null };

      const { decryptedFields, decryptedBankReferences, decryptedInvitedSignatures } =
        await decryptCreditApplicationData(creditApplicationCopy, decryptValue);

      if (isMounted) {
        setDecryptedCreditApplication({
          ...creditApplicationCopy,
          fieldValues: decryptedFields,
          invitedSignatures: decryptedInvitedSignatures,
        });
      }
    };

    decrypt();

    return () => {
      isMounted = false;
    };
  }, [creditApplication, applicationHistoryId]);

  const duplicateWithNextGroupedPosition = (allFields: GroupedCreditApplicationFields[]) => {
    const groupedFields = allFields.filter((f) => f.groupedPosition !== undefined);

    // Find the current max grouped position
    const maxGroupPos = Math.max(...groupedFields.map((f) => f.groupedPosition ?? 0));

    // Get only fields of the last group
    const lastGroupFields = groupedFields.filter((f) => f.groupedPosition === maxGroupPos);

    const nextGroupedPosition = maxGroupPos + 1;

    const newGroup = lastGroupFields.map((field) => {
      // Strip existing `-group-X` suffix
      const cleanFieldId = field.id.replace(/-group-\d+$/, '');
      const clonedFields = field.fields.map((f) => {
        const cleanId = f.id.replace(/-group-\d+$/, '');
        return {
          ...f,
          id: `${cleanId}-group-${nextGroupedPosition}`,
        };
      });

      return {
        ...field,
        id: `${cleanFieldId}-group-${nextGroupedPosition}`,
        groupedPosition: nextGroupedPosition,
        fields: clonedFields,
      };
    });

    return [...allFields, ...newGroup];
  };
  useEffect(() => {
    if (basicSupplier && basicSupplier.logo && basicSupplier.logo?.url) {
      setDomainIdUrl(basicSupplier.logo?.url);
    }
  }, [basicSupplier]);

  useEffect(() => {
    if (basicSupplier) {
      const branches = (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .map((branch) => ({ name: branch?.branchName ?? '', erpId: branch?.erpId ?? '' }));
      const seenSalesReps = new Set<string>();
      // Scoped to the application's own assigned branch when it has one — a rep from some other
      // branch isn't a valid choice here. Falls back to every branch's reps when unassigned.
      const salesReps = (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .filter(
          (branch) => !creditApplication?.branchId || branch.branchId === creditApplication.branchId
        )
        .flatMap((branch) => branch?.salesRep ?? [])
        .filter((rep) => {
          const name = rep?.name ?? '';
          if (!name || seenSalesReps.has(name)) return false;
          seenSalesReps.add(name);
          return true;
        })
        .map((rep) => ({
          name: rep?.name ?? '',
          erpId: rep?.erpId ?? '',
          isHouseRep: rep?.isHouseRep ?? false,
        }));
      setDomainDetails({
        url: basicSupplier?.logo?.url ?? '',
        name: basicSupplier?.name ?? '',
        id: basicSupplier?.id,
        domainId: basicSupplier?.domainId,
        branches,
        salesReps,
      });
    }
  }, [basicSupplier, creditApplication?.branchId]);

  const applicationTitle = useMemo(() => {
    if (creditApplicationTemplate) {
      return getCreditApplicationTypeLabel(creditApplicationTemplate.applicationType, {
        long: true,
        formName: creditApplicationTemplate.name,
      });
    }
    return ' Application';
  }, [creditApplicationTemplate]);

  const hasFormChanges = useMemo(() => {
    if (!initialFormData) return false;

    const currentSnapshot = buildFormSnapshot(
      allStepFieldsValues,
      bankReference,
      invitedSignatures
    );

    return JSON.stringify(initialFormData) !== JSON.stringify(currentSnapshot);
  }, [initialFormData, allStepFieldsValues, bankReference, invitedSignatures]);

  useEffect(() => {
    if (!initialFormData) return;
    const currentCompanyNameField = allStepFieldsValues.find(
      (f) => f.id === 'customer-or-company-name'
    );
    const originalCompanyNameSnapshot = initialFormData.find(
      (f) => f.name === 'customer-or-company-name'
    );
    if (!currentCompanyNameField || !originalCompanyNameSnapshot) return;
    const companyNameChanged =
      JSON.stringify(originalCompanyNameSnapshot.value) !==
      JSON.stringify(currentCompanyNameField.value);
    if (!companyNameChanged) return;

    setInvitedSignatures((prev) => {
      let didInvalidateAny = false;
      const next = prev.map((sig) => {
        if (!sig.signature) return sig;
        didInvalidateAny = true;
        return { ...sig, signature: '', dateSigned: '', ipAddress: '', location: '' };
      });
      return didInvalidateAny ? next : prev;
    });
  }, [allStepFieldsValues, initialFormData]);

  const handleCheckLogin = async (resendEmail?: boolean): Promise<boolean> => {
    const email = personalDetailsValues.find(
      (f) => f.fromField === 'TEXT' && f.id === 'email-personal'
    )?.value;

    if (!email) {
      if (resendEmail) {
        openDillToast({
          type: 'ERROR',
          message: 'Email is required to check user login.',
        });
      }
      return false;
    }
    setUserEmail(email);
    localStorage.setItem('emailForSignIn', email);
    const firstName =
      personalDetailsValues.find((f) => f.fromField === 'TEXT' && f.id === 'first-name-personal')
        ?.value || '';
    const lastName =
      personalDetailsValues.find((f) => f.fromField === 'TEXT' && f.id === 'last-name-personal')
        ?.value || '';

    try {
      const checkFirebaseUserResponse = await dispatch(
        checkFirebaseUser({ email, supplierName: '' })
      );
      if (resendEmail) {
        openDillToast({
          type: 'SUCCESS',
          message: 'A verification email has been resent.',
        });
      }

      if (checkFirebaseUserResponse.type === 'auth/checkFirebaseUser/fulfilled') {
        const payload = checkFirebaseUserResponse.payload as {
          data: {
            emailVerified: boolean;
            metadata: { lastSignInTime: string | null };
            uid: string;
            customToken?: string;
            providerData?: { providerId: string }[];
          };
        };

        const { emailVerified, metadata, providerData } = payload.data;
        const lastSignIn = metadata?.lastSignInTime ? new Date(metadata.lastSignInTime) : null;
        const now = new Date();

        const isInactive = !lastSignIn || now.getTime() - lastSignIn.getTime() > 3 * 60 * 60 * 1000;
        const hasPasswordProvider = providerData?.some((p) => p.providerId === 'password') ?? false;

        if (!emailVerified || isInactive) {
          const sendLinkResponse = await dispatch(
            unverifyFirebaseUserAndSendLink({
              email,
              domainId: '',
              firstName,
              lastName,
              templateId: creditApplicationTemplate?.id || '',
              applicationId: creditApplicationId,
              type: updateType ? updateType : 'review',
              ...(hasPasswordProvider ? { skipUnverify: true } : {}),
              ...(resendEmail ? { overrideCooldown: true } : {}),
            })
          );
          if (sendLinkResponse.type !== 'auth/unverifyFirebaseUserAndSendLink/fulfilled') {
            openDillToast({
              type: 'ERROR',
              message: `Failed to send verification link to ${email}.`,
            });
            return false;
          }

          setModalState(ModalState.VERIFY_LOGIN);
          return false;
        } else if (emailVerified && payload.data?.customToken) {
          await dispatch(
            firebaseSignInWithCustomToken({
              token: payload.data.customToken,
            })
          );
          const decodedUrl = decodeURIComponent(window.location.href);
          const parsedUrl = new URL(decodedUrl);

          const domainId = parsedUrl.searchParams.get('domainId');
          const templateId = parsedUrl.searchParams.get('templateId');
          const updateTypeParam = parsedUrl.searchParams.get('updateType');
          const historyId = parsedUrl.searchParams.get('historyId');

          const cleanUrl = new URL(window.location.origin + window.location.pathname);
          if (domainId) cleanUrl.searchParams.set('domainId', domainId);
          if (templateId) cleanUrl.searchParams.set('templateId', templateId);
          if (updateTypeParam) cleanUrl.searchParams.set('updateType', updateTypeParam);
          if (historyId) cleanUrl.searchParams.set('historyId', historyId);

          window.history.replaceState({}, document.title, cleanUrl.toString());
        }

        return true;
      }

      const response = await dispatch(
        createCreditAppFirebaseUser({
          email,
          domainId: '',
          firstName,
          lastName,
          templateId: creditApplicationTemplate?.id || '',
          applicationId: creditApplicationId,
          type: updateType ? updateType : 'review',
        })
      );
      if (response.type !== 'auth/createCreditAppFirebaseUser/fulfilled') {
        openDillToast({
          type: 'ERROR',
          message: `Failed to create user`,
        });
        return false;
      }
      setModalState(ModalState.VERIFY_LOGIN);

      return false;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error during Firebase user check:', error);
      return false;
    }
  };

  useEffect(() => {
    if (creditApplicationTemplate) {
      const PERSONAL_IDS = ['email-personal', 'first-name-personal', 'last-name-personal'];

      const personFields = creditApplicationTemplate.steps.flatMap((st) =>
        st.fields.flatMap((field) => {
          if (field.stepType !== 'NOT_GROUPED' || !field.unGroupedFields) return [];

          return field.unGroupedFields
            .filter((f) => PERSONAL_IDS.includes(f.id))
            .map((f) => {
              const data = applyFieldData({
                st,
                field,
                f,
                groupedPosition: 0,
                index: 0,
              });

              // Override user profile defaults
              if (f.id === 'email-personal') data.value = userEmail || data.value || '';
              if (f.id === 'first-name-personal') data.value = userFirstName || data.value || '';
              if (f.id === 'last-name-personal') data.value = userLastName || data.value || '';

              return data;
            });
        })
      );

      setPersonalDetailsValues(personFields);
    }
  }, [userFirstName, userLastName, decryptedCreditApplication, creditApplicationTemplate]);

  const getSourceApplication = () => {
    if (!creditApplication) return null;
    if (applicationHistoryId && creditApplication.applicationUpdateHistory) {
      const record = creditApplication.applicationUpdateHistory.find(
        (h: any) => h.id === applicationHistoryId
      );
      if (record) return record as typeof creditApplication;
    }
    return creditApplication;
  };

  const handleSubmitLogin = async () => {
    const isLoginChecked = await handleCheckLogin();
    if (!isLoginChecked) {
      return;
    }
    const sourceApp = getSourceApplication();
    const feedbackFields = sourceApp?.feedbackFields || [];

    if (feedbackFields.length === 0 && updateType !== 'update') {
      setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
      setModalDetails({
        modalType: 'Success',
        title: 'Fields Updated',
        details: `Your fields have been updated in the ${applicationTitle.toLowerCase()}.`,
      });
      return;
    }

    const statusAllowsUpdate =
      sourceApp?.status === 'AWAITING_SIGNATURE' ||
      sourceApp?.status === 'INTERNAL_SIGNATURE' ||
      sourceApp?.status === 'SUBMITTED' ||
      sourceApp?.status === 'READY_FOR_DECISION';

    if (updateType === 'update' && !statusAllowsUpdate) {
      const isNotYetSubmitted = ['OPENED', 'SENT', 'DRAFT'].includes(sourceApp?.status ?? '');
      setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
      setModalDetails({
        modalType: 'Error',
        title: 'Unable to Update Application',
        details: isNotYetSubmitted
          ? `Updates are not available for this ${applicationTitle.toLowerCase()}. Make sure to submit the application to enable updates.`
          : `A decision has already been made for this ${applicationTitle.toLowerCase()}. Please contact the supplier if you want to make changes to your application.`,
      });
      return;
    }

    if (!isSaved) {
      setModalState(ModalState.UPDATE_FIELDS);
    }
    setErrors([]);
  };

  const getFirebaseConfirmLoginUser = async (url: string) => {
    const result = await dispatch(firebaseConfirmLoginUser({ url }));
    if (result.type === 'auth/firebaseConfirmLoginUser/fulfilled') {
      const decodedUrl = decodeURIComponent(url);
      const parsedUrl = new URL(decodedUrl);

      const domainId = parsedUrl.searchParams.get('domainId');
      const templateId = parsedUrl.searchParams.get('templateId');
      const historyId = parsedUrl.searchParams.get('historyId');

      const cleanUrl = new URL(window.location.origin + window.location.pathname);
      if (domainId) cleanUrl.searchParams.set('domainId', domainId);
      if (templateId) cleanUrl.searchParams.set('templateId', templateId);
      if (historyId) cleanUrl.searchParams.set('historyId', historyId);

      window.history.replaceState({}, document.title, cleanUrl.toString());

      await handleSubmitLogin();
    } else {
      setModalState(ModalState.VERIFY_LOGIN);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlModeValue = params.get('mode');
    const apiKey = params.get('apiKey');
    const firstName = params.get('firstName');
    const lastName = params.get('lastName');
    const applicationId = params.get('applicationId');
    const updateTypeParam = params.get('updateType');
    const paramDomainId = params.get('domainId');
    const historyIdParam = params.get('historyId');
    if (paramDomainId) {
      fetchDataUsingDomainId(paramDomainId);
    }
    if (historyIdParam) {
      setApplicationHistoryId(historyIdParam);
    }
    if (firstName) {
      setUserFirstName(firstName);
    }
    if (lastName) {
      setUserLastName(lastName);
    }
    if (applicationId) {
      setCreditApplicationId(applicationId);
    }
    if (updateTypeParam) {
      setUpdateType(updateTypeParam);
    }
    if (urlModeValue === 'signIn' && apiKey) {
      localStorage.setItem('newSignIn', 'newSignIn');
      const emailForSignIn = localStorage.getItem('emailForSignIn');
      if (emailForSignIn) {
        setUserEmail(emailForSignIn);
        getFirebaseConfirmLoginUser(window.location.href);
      } else {
        loginCheckInitiatedRef.current = true;
        verificationFailedRef.current = true;
        setModalState(ModalState.VERIFY_LOGIN);
        openDillToast({
          type: 'ERROR',
          message:
            'Failed to verify your email. You might have opened the verification link for a different browser. You can open the verification link with the previous browser',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (personalDetailsValues && !loginCheckInitiatedRef.current) {
      const email = personalDetailsValues.find(
        (f) => f.fromField === 'TEXT' && f.id === 'email-personal'
      )?.value;
      if (email) {
        loginCheckInitiatedRef.current = true;
        handleSubmitLogin();
      }
    }
  }, [personalDetailsValues]);

  const initializeCreditApp = async (bankRefs: BankReference[]) => {
    if (!bankRefs || bankRefs.length === 0) return;

    const decryptedBankRefs = await Promise.all(
      bankRefs.map(async (bankRef) => {
        // Strip the bank reference's own audit-trail array before holding it in this
        // form's state — resubmitting it risks the round-trip date corruption (Firestore
        // Timestamp -> JSON string -> never converted back) this form doesn't need to own.
        const { approvalStatusHistory, ...bankRefWithoutHistory } = bankRef as any;
        const token = await decryptValue(bankRef.bankDetails.accountNumber, true);
        return {
          ...bankRefWithoutHistory,
          bankDetails: {
            ...bankRef.bankDetails,
            accountNumber: token,
          },
        };
      })
    );

    setBankReference(decryptedBankRefs);
  };

  const extractInviteUsers = (
    indx: number,
    whoNeedsToSign?: string,
    name?: string,
    type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR',
    minimumCreditAmount?: number,
    fields?: DynamicInvitedSignaturesFields[],
    minimumNumberOfSignatures?: number,
    isRequired?: boolean,
    linkedFieldId?: string | null,
    id?: string,
    signaturePageFieldIds?: string | null
  ): {
    title: string;
    email: string;
    step: number;
    signatureGroupName?: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    fields: DynamicInvitedSignaturesFields[];
    minimumNumberOfSignatures?: number;
    isRequired?: boolean;
    linkedFieldId?: string;
    id?: string;
    signaturePageFieldIds?: string | null;
  }[] => {
    if (!whoNeedsToSign) return [];
    return whoNeedsToSign.split(',').map((option) => ({
      title: '',
      email: '',
      step: indx,
      signatureGroupName: name,
      placeHolder: option.trim(),
      type: type ?? 'AUTHORIZED_AGENT',
      minimumCreditAmount,
      fields: fields || [],
      minimumNumberOfSignatures,
      isRequired,
      id,
      ...(linkedFieldId ? { linkedFieldId } : {}),
      ...(signaturePageFieldIds ? { signaturePageFieldIds } : {}),
    }));
  };

  useEffect(() => {
    if (!creditApplication || !creditApplicationTemplate) return;
    if (verificationFailedRef.current) return;

    const sourceApp = getSourceApplication();
    let feedbackFields = sourceApp?.feedbackFields || [];

    if (feedbackFields.length === 0 && updateType !== 'update') {
      setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
      setModalDetails({
        modalType: 'Success',
        title: 'Fields Updated',
        details: `Your fields have been updated in the ${applicationTitle.toLowerCase()}.`,
      });
      return;
    }

    if (
      (sourceApp?.status === 'AWAITING_SIGNATURE' ||
        sourceApp?.status === 'INTERNAL_SIGNATURE' ||
        sourceApp?.status === 'SUBMITTED' ||
        sourceApp?.status === 'READY_FOR_DECISION') &&
      updateType === 'update'
    ) {
      console.log('Using feedback fields for update');
      feedbackFields = (sourceApp?.fieldValues || [])
        .filter((f) => f.stepNumber !== 0)
        .map((f) => ({
          name: f.name,
          uniqueId: f.uniqueId,
          id: f.id,
        }));
    } else if (updateType === 'update') {
      const isNotYetSubmitted = ['OPENED', 'SENT', 'DRAFT'].includes(sourceApp?.status ?? '');

      console.log('Feedback fields found:', { isNotYetSubmitted });
      setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
      setModalDetails({
        modalType: 'Error',
        title: 'Unable to Update Application',
        details: isNotYetSubmitted
          ? `Updates are not available for this ${applicationTitle.toLowerCase()}. Make sure to submit the application to enable updates.`
          : `A decision has already been made for this ${applicationTitle.toLowerCase()}. Please contact the supplier if you want to make changes to your application.`,
      });
      return;
    } else if (feedbackFields.length === 0) {
      console.log(
        'No feedback fields found, but updateType is not "update". Proceeding with success modal.'
      );
      setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
      setModalDetails({
        modalType: 'Success',
        title: 'Fields Updated',
        details: `Your fields have been updated in the ${applicationTitle.toLowerCase()}.`,
      });
      return;
    }

    const steps = creditApplicationTemplate.steps || [];
    const stepFields: any[] = [];

    const allTemplateFields: any[] = [];
    for (const step of steps) {
      for (const field of step.fields) {
        if (field.unGroupedFields?.length) allTemplateFields.push(...field.unGroupedFields);
        if (field.groupedFields?.length) {
          for (const group of field.groupedFields) allTemplateFields.push(...group.fields);
        }
      }
    }

    const isFeedbackMatch = (id: string) =>
      feedbackFields.some((fb) => typeof fb?.id === 'string' && fb.id.includes(id));

    const conditionallyMatchedIds = new Set<string>();
    let addedNewMatch = true;
    while (addedNewMatch) {
      addedNewMatch = false;
      for (const f of allTemplateFields) {
        const sourceFieldId = (f as any)?.conditionalVisibility?.sourceFieldId;
        if (
          typeof f?.id === 'string' &&
          sourceFieldId &&
          !conditionallyMatchedIds.has(f.id) &&
          !isFeedbackMatch(f.id) &&
          (isFeedbackMatch(sourceFieldId) || conditionallyMatchedIds.has(sourceFieldId))
        ) {
          conditionallyMatchedIds.add(f.id);
          addedNewMatch = true;
        }
      }
    }

    const isFieldMatched = (id: string) => isFeedbackMatch(id) || conditionallyMatchedIds.has(id);

    for (const step of steps) {
      const creditApplicationFields = step.fields;
      const currentFieldsFields: any[] = [];

      for (const field of creditApplicationFields) {
        let hasMatch = false;
        const clonedField = { ...field };

        if (field.unGroupedFields?.length) {
          const matchedUngrouped = field.unGroupedFields.filter(
            (ug) => typeof ug?.id === 'string' && isFieldMatched(ug.id)
          );

          if (matchedUngrouped.length > 0) {
            hasMatch = true;
            clonedField.unGroupedFields = matchedUngrouped.map((ug) => {
              let onlyShowPlaidOption = ug.onlyShowPlaidOption;
              let isRequired = ug.isRequired;
              const currentFeedBackFields = sourceApp?.feedbackFields || [];
              if (currentFeedBackFields.length > 0) {
                const bankRefFields = currentFeedBackFields.find(
                  (ffield) =>
                    typeof ug?.id === 'string' &&
                    typeof ffield?.id === 'string' &&
                    ug.id.includes('bank-reference') &&
                    ug.id === ffield.id
                );
                if (bankRefFields && bankRefFields.onlyShowPlaidOption) {
                  onlyShowPlaidOption = true;
                  isRequired = true;
                }
              }
              return {
                ...ug,
                ...(onlyShowPlaidOption ? { onlyShowPlaidOption: true } : {}),
                ...(isRequired ? { isRequired: true } : {}),
              };
            });
          } else {
            clonedField.unGroupedFields = [];
          }
        }

        if (field.groupedFields?.length) {
          const basePositionGroups = field.groupedFields.filter(
            (group) => (group.groupedPosition ?? 0) === 0
          );
          const baseFieldIds = basePositionGroups.flatMap((group) =>
            group.fields.map((gf) => gf?.id).filter((id): id is string => typeof id === 'string')
          );

          const matchedGroupPositions = new Set<number>();
          for (const baseId of baseFieldIds) {
            for (const fb of feedbackFields) {
              if (typeof fb?.id !== 'string' || !fb.id.includes(baseId)) continue;
              const suffixMatch = fb.id.match(/-group-(\d+)$/);
              matchedGroupPositions.add(suffixMatch ? parseInt(suffixMatch[1], 10) : 0);
            }
          }

          if (matchedGroupPositions.size > 0) {
            hasMatch = true;
            clonedField.groupedFields = Array.from(matchedGroupPositions)
              .sort((a, b) => a - b)
              .flatMap((position) =>
                position === 0
                  ? basePositionGroups
                  : basePositionGroups.map((group) => {
                      const cleanGroupId = group.id.replace(/-group-\d+$/, '');
                      const clonedFields = group.fields.map((f) => {
                        const cleanId =
                          typeof f?.id === 'string' ? f.id.replace(/-group-\d+$/, '') : f?.id;
                        return { ...f, id: `${cleanId}-group-${position}` };
                      });
                      return {
                        ...group,
                        id: `${cleanGroupId}-group-${position}`,
                        groupedPosition: position,
                        fields: clonedFields,
                      };
                    })
              );
          } else {
            clonedField.groupedFields = [];
          }
        }

        if (hasMatch) {
          currentFieldsFields.push(clonedField);
        }
      }

      if (currentFieldsFields.length > 0) {
        stepFields.push({
          ...step,
          fields: currentFieldsFields,
        });
      }
    }

    if (sourceApp?.bankReference) {
      if (feedbackFields.length > 0) {
        const bankRefFields = feedbackFields.find((f) => f.id.includes('bank-reference'));
        if (bankRefFields && bankRefFields.onlyShowPlaidOption !== true) {
          initializeCreditApp(sourceApp.bankReference);
        }
      } else {
        initializeCreditApp(sourceApp.bankReference);
      }
    }

    const specificRules = creditApplication?.specificRules ?? [];
    const stepsWithOverrides = stepFields.map((step: any) => ({
      ...step,
      fields: step.fields.map((field: any) => {
        if (field.unGroupedFields?.length) {
          return {
            ...field,
            unGroupedFields: field.unGroupedFields.map((f: any) => {
              const rule = specificRules.find((r: any) => r.fieldId === f.id);
              if (!rule) return f;
              return {
                ...f,
                ...(rule.isRequired !== undefined ? { isRequired: rule.isRequired } : {}),
                ...(f.fromField === 'INVITE_USER' && rule.minimumNumberOfSignatures !== undefined
                  ? { minimumNumberOfSignatures: rule.minimumNumberOfSignatures }
                  : {}),
                ...(f.fromField === 'INVITE_USER' && rule.minimumCreditAmount !== undefined
                  ? { minimumCreditAmount: rule.minimumCreditAmount }
                  : {}),
                ...(rule.hidden !== undefined
                  ? {
                      hidden: rule.hidden,
                      conditionalVisibility: rule.hidden
                        ? { sourceFieldId: '__specific_rule_hidden__', sourceOptionValues: [] }
                        : null,
                    }
                  : {}),
              };
            }),
          };
        }
        if (field.groupedFields?.length) {
          const groupRule = specificRules.find(
            (r: any) => r.fieldId === `group:${field.groupName}`
          );
          if (groupRule?.minimumNumberOfGroups !== undefined) {
            if (groupRule.minimumNumberOfGroups === 0) {
              return { ...field, groupedFields: [] };
            }
            const currentGroupCount = Math.max(
              ...field.groupedFields.map((item: any) => item.groupedPosition ?? 0)
            );
            const minimumGroups = groupRule.minimumNumberOfGroups - 1;
            if (currentGroupCount < minimumGroups) {
              let updatedGroupedFields = [...field.groupedFields];
              for (let i = 0; i < minimumGroups - currentGroupCount; i++) {
                updatedGroupedFields = duplicateWithNextGroupedPosition(updatedGroupedFields);
              }
              return { ...field, groupedFields: updatedGroupedFields };
            }
          }
        }
        return field;
      }),
    }));
    setFetchedCreditApplicationTemplate(stepsWithOverrides);

    const inviteData: {
      title: string;
      email: string;
      step: number;
      fullNameNeeded?: boolean;
      driverLicenseNeeded?: boolean;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      linkedFieldId?: string;
    }[] = [];

    creditApplicationTemplate.steps.forEach((stp, index) => {
      stp.fields?.forEach((field) => {
        if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
          field.unGroupedFields.forEach((f) => {
            if (f.fromField === 'INVITE_USER') {
              inviteData.push(
                ...extractInviteUsers(
                  index,
                  f.whoNeedsToSign,
                  f.name,
                  f.type,
                  f.minimumCreditAmount,
                  f.invitedSignaturesFields || [],
                  f.minimumNumberOfSignatures,
                  f.isRequired,
                  f?.linkedFieldId,
                  f?.id,
                  f.signaturePageFieldIds
                )
              );
            }
          });
        }

        if (field.stepType === 'GROUPED' && field.groupedFields) {
          field.groupedFields.forEach((group) => {
            group.fields.forEach((f) => {
              if (f.fromField === 'INVITE_USER') {
                inviteData.push(
                  ...extractInviteUsers(
                    index,
                    f.whoNeedsToSign,
                    f.name,
                    f.type,
                    f.minimumCreditAmount,
                    f.invitedSignaturesFields || [],
                    f.minimumNumberOfSignatures,
                    f.isRequired,
                    f?.linkedFieldId,
                    f?.id,
                    f.signaturePageFieldIds
                  )
                );
              }
            });
          });
        }
      });
    });

    setInvitedSignatures(inviteData);
    setInvitedSignaturesErrors(
      inviteData.map((item) => ({ placeHolder: item?.placeHolder ?? '', errorText: '' }))
    );
  }, [creditApplication, creditApplicationTemplate, applicationHistoryId]);

  useEffect(() => {
    if (!fetchedCreditApplicationTemplate) return;
    const allStepFields = fetchedCreditApplicationTemplate.flatMap((st, index) => {
      return st.fields.flatMap((field) => {
        if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
          return field.unGroupedFields.map((f) => {
            const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
              field.columnType
            } ${0} ${f.id || ''}`
              .toLowerCase()
              .replace(/\s+/g, '-');
            let value =
              f.fromField === 'TEXT' ||
              f.fromField === 'TEXT_AREA' ||
              f.fromField === 'OPTION_FIELD' ||
              f.fromField === 'NUMBER' ||
              f.fromField === 'CURRENCY'
                ? ''
                : f.fromField === 'CHECK_BOX'
                ? false
                : null;
            const foundField = (decryptedCreditApplication?.fieldValues || []).find(
              (stFd) => stFd.uniqueId === uniqueId
            );
            const fieldValue = foundField?.fieldValue ?? null;
            if (foundField) {
              value = foundField.value;
              if (f.fromField === 'CURRENCY' && value !== null && value !== '') {
                const parsed = parseFloat(String(value).replace(/,/g, ''));
                if (!isNaN(parsed)) value = parsed as any;
              }
            }
            if (f.fromField === 'DOCUMENT_FIELD') {
              value = (f as any).documentText || value || '';
            }
            let onlyShowPlaidOption = f.onlyShowPlaidOption;
            const currentFeedBackFields = getSourceApplication()?.feedbackFields || [];
            if (currentFeedBackFields.length > 0) {
              const bankRefFields = currentFeedBackFields.find(
                (ffield) => f.id.includes('bank-reference') && f.id === ffield.id
              );
              if (bankRefFields && bankRefFields.onlyShowPlaidOption) {
                onlyShowPlaidOption = true;
              }
            }
            return {
              uniqueId: uniqueId,
              id: f.id || '',
              name: f.name || '',
              fromField: f.fromField || '',
              isRequired: f.isRequired || false,
              isHashed: f.isHashed || false,
              stepName: st.name,
              stepNumber: st.position || 0,
              groupStepPosition: field.position || 0,
              groupedPosition: 0,
              groupName: field?.groupName || '',
              groupColumnType: field.columnType || ('ONE_COLUMN' as const),
              isFieldArray: false,
              isFieldEmail: f.isFieldEmail || false,
              index: 0,
              value: value,
              ...(f.optionType ? { optionType: f.optionType } : {}),
              ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
              ...(f.status ? { status: f.status } : {}),
              ...(f.emailDate ? { emailDate: f.emailDate } : {}),
              ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
              ...(f.type ? { type: f.type } : {}),
              ...(fieldValue ? { fieldValue } : {}),
              ...(onlyShowPlaidOption ? { onlyShowPlaidOption: true } : {}),
              ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
              ...((f as any).conditionalVisibility
                ? { conditionalVisibility: (f as any).conditionalVisibility }
                : {}),
              ...((f as any).currencyDecimalPlaces !== undefined
                ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                : {}),
              ...(f.borderId ? { borderId: f.borderId } : {}),
              ...((f as any).formTemplate ? { formTemplate: (f as any).formTemplate } : {}),
            };
          });
        }

        if (field.stepType === 'GROUPED' && field.groupedFields) {
          const groupFieldEntries = field.groupedFields.flatMap((group) =>
            group.fields.map((f) => {
              const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                field.columnType
              } ${group.groupedPosition} ${f.id || ''}`
                .toLowerCase()
                .replace(/\s+/g, '-');
              let value =
                f.fromField === 'TEXT' ||
                f.fromField === 'TEXT_AREA' ||
                f.fromField === 'OPTION_FIELD' ||
                f.fromField === 'NUMBER' ||
                f.fromField === 'CURRENCY'
                  ? ''
                  : f.fromField === 'CHECK_BOX'
                  ? false
                  : null;
              const foundField = (decryptedCreditApplication?.fieldValues || []).find(
                (stFd) => stFd.id === f.id && stFd.groupedPosition === group.groupedPosition
              );
              const fieldValue = foundField?.fieldValue ?? null;
              if (foundField) {
                value = foundField.value;
                if (f.fromField === 'CURRENCY' && value !== null && value !== '') {
                  const parsed = parseFloat(String(value).replace(/,/g, ''));
                  if (!isNaN(parsed)) value = parsed as any;
                }
              }
              if (f.fromField === 'DOCUMENT_FIELD') {
                value = (f as any).documentText || value || '';
              }
              return {
                uniqueId: uniqueId,
                id: f.id || '',
                name: f.name || '',
                fromField: f.fromField || '',
                isRequired: f.isRequired || false,
                isHashed: f.isHashed || false,
                stepName: st.name,
                stepNumber: st.position || 0,
                groupStepPosition: field.position || 0,
                groupedPosition: group.groupedPosition || 0,
                groupName: field?.groupName || '',
                groupColumnType: group.columnType || ('ONE_COLUMN' as const),
                isFieldArray: field.isFieldArray || false,
                isFieldEmail: f.isFieldEmail || false,
                index: group.index || 0,
                value: value,
                ...(f.optionType ? { optionType: f.optionType } : {}),
                ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                ...(f.status ? { status: f.status } : {}),
                ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
                ...(f.type ? { type: f.type } : {}),
                ...(fieldValue ? { fieldValue } : {}),
                ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
                ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
                ...((f as any).conditionalVisibility
                  ? { conditionalVisibility: (f as any).conditionalVisibility }
                  : {}),
                ...((f as any).currencyDecimalPlaces !== undefined
                  ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                  : {}),
                ...(f.borderId ? { borderId: f.borderId } : {}),
                ...((f as any).formTemplate ? { formTemplate: (f as any).formTemplate } : {}),
              };
            })
          );

          if (field.hasTradeReferenceUpload) {
            const ocrUploadId = `${field.groupName}-ocr-upload`.toLowerCase().replace(/\s+/g, '-');
            const ocrUniqueId =
              `${st.name} ${field?.groupName} ${field.position} ${field.columnType} 0 ocr-upload`
                .toLowerCase()
                .replace(/\s+/g, '-');
            const foundUploadField = (decryptedCreditApplication?.fieldValues || []).find(
              (stFd) => stFd.uniqueId === ocrUniqueId
            );
            groupFieldEntries.push({
              uniqueId: ocrUniqueId,
              id: ocrUploadId,
              name: `${field?.groupName ?? ''} AI Upload`,
              fromField: 'TRADE_REFERENCE',
              isRequired: false,
              isHashed: false,
              stepName: st.name,
              stepNumber: st.position || 0,
              groupStepPosition: field.position || 0,
              groupedPosition: 0,
              groupName: field?.groupName || '',
              groupColumnType: 'ONE_COLUMN' as const,
              isFieldArray: field.isFieldArray || false,
              isFieldEmail: false,
              index: 0,
              value: foundUploadField?.value ?? null,
            });
          }

          return groupFieldEntries;
        }

        return [];
      });
    });
    setAllStepFieldsValues(allStepFields);
  }, [fetchedCreditApplicationTemplate, decryptedCreditApplication]);

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const id = pathlist[pathlist.length - 1];

    fetchData(id);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramTemplateId = params.get('templateId');
    if (paramTemplateId) {
      fetchDataUsingTemplateId(paramTemplateId);
    }
  }, [location.search]);

  const onChangeFormData = ({
    id,
    value,
    secondaryValue,
    fieldValue,
    addressVerification,
  }: {
    id: string;
    value: any;
    secondaryValue?: any;
    fieldValue?: ConditionalOptionValues[];
    addressVerification?: { verified: boolean; standardizedAddress?: string | null } | null;
  }) => {
    const uniqueId = allStepFieldsValues.find((f) => f.uniqueId === id)?.uniqueId;
    if (uniqueId) {
      setAllStepFieldsValues((prev) =>
        prev.map((f) =>
          f.uniqueId === id
            ? {
                ...f,
                value: value,
                ...(secondaryValue !== undefined ? { secondaryValue: secondaryValue } : {}),
                ...(fieldValue !== undefined ? { fieldValue: fieldValue } : {}),
                ...(addressVerification !== undefined ? { addressVerification } : {}),
              }
            : f
        )
      );
      setErrors((prev) => prev.filter((f) => f.uniqueId !== id));
    }
  };

  const getFieldAndUpdate = async () => {
    if (!creditApplication) return;
    const sourceApp = getSourceApplication();
    const decryptedFields = await Promise.all(
      (sourceApp?.fieldValues ?? creditApplication.fieldValues).map(async (field) => {
        let newField = { ...field };

        if (Array.isArray(newField.fieldValue)) {
          newField = {
            ...newField,
            fieldValue: await Promise.all(
              newField.fieldValue.map(async (value) => {
                if (value.isHashed) {
                  const fValue = await decryptValue(value.value, true);
                  return { ...value, value: fValue };
                }
                return value;
              })
            ),
          };
        }

        if (newField.isHashed && newField.value) {
          const decryptedValue = await decryptValue(newField.value, true);
          newField = { ...newField, value: decryptedValue };
        }

        return newField;
      })
    );
    setAllStepFieldsValues((prev) =>
      prev.map((f) => {
        const findVal = decryptedFields.find((fld) => f.uniqueId === fld.uniqueId);
        if (!findVal) return f;

        return {
          ...f,
          value: findVal.value,
          ...(findVal.secondaryValue !== undefined
            ? { secondaryValue: findVal.secondaryValue }
            : {}),
          ...(findVal.fieldValue !== undefined ? { fieldValue: findVal.fieldValue } : {}),
        };
      })
    );

    const sourceInvitedSignatures =
      sourceApp?.invitedSignatures ?? creditApplication.invitedSignatures;
    if (sourceInvitedSignatures) {
      const decryptedInvitedSignatures = await Promise.all(
        sourceInvitedSignatures.map(async (sig: any) => {
          const decryptedSigFields = await Promise.all(
            (sig.fields ?? []).map(async (field: any) => {
              if (field.isHashed && field.value) {
                const decryptedValue = await decryptValue(field.value, true);
                return { ...field, value: decryptedValue };
              }
              return field;
            })
          );
          return { ...sig, fields: decryptedSigFields };
        })
      );
      setInvitedSignatures((prev) =>
        prev.map((f) => {
          const match = decryptedInvitedSignatures.find(
            (invite: any) => invite.placeHolder === f.placeHolder && invite.type === f.type
          );
          return match ? { ...f, ...match } : f;
        })
      );
    }
  };

  useEffect(() => {
    getFieldAndUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplication, creditApplicationTemplate, applicationHistoryId]);

  const validateForm = () => {
    const currentStepFields = allStepFieldsValues.filter((f) => f.fromField !== 'BANK_REFERENCE');

    const fieldErrors: { uniqueId: string; errorText: string }[] = [];

    for (const field of currentStepFields) {
      // Required field check
      let value = field.value;
      if (typeof value === 'string') {
        value = value.trim();
      }
      if (field.isRequired && (value === '' || value === null)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} is required.`,
        });
      }

      // Email format validation — matches by isFieldEmail flag OR by field id containing 'email'
      // (similar to how email-personal is identified by id)
      else if (
        field.fromField === 'TEXT' &&
        (field.isFieldEmail || field.id.includes('email')) &&
        field.value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.toString())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be a valid email.`,
        });
      }

      // Validate email fields nested inside fieldValue (e.g. trade references)
      if (field.fieldValue && Array.isArray(field.fieldValue)) {
        for (const subField of field.fieldValue) {
          if (!subField) continue;
          const isSubEmailField =
            subField.fromField === 'TEXT' &&
            (subField.isFieldEmail || subField.id?.includes('email'));
          if (
            isSubEmailField &&
            subField.value &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subField.value.toString())
          ) {
            fieldErrors.push({
              uniqueId: subField.uniqueId ?? field.uniqueId,
              errorText: `${subField.name} must be a valid email.`,
            });
          }
        }
      }
    }

    // Trade reference email validations
    const personalEmail = getSourceApplication()
      ?.fieldValues.find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase();

    const tradeRefEmailFields = currentStepFields.filter(
      (f) => f.id.includes('trade-reference-company-email') && f.fromField === 'TEXT' && f.value
    );

    // Neither the supplier's own staff nor the applicant's own coworkers can vouch for the
    // applicant — see getBlockedTradeReferenceEmailDomains.
    const blockedTradeRefDomains = getBlockedTradeReferenceEmailDomains(basicSupplier, [
      userEmail,
      getSourceApplication()?.senderEmail,
      personalEmail,
    ]);

    const seenTradeEmails: string[] = [];
    for (const field of tradeRefEmailFields) {
      const emailVal = field.value.toString().trim().toLowerCase();

      if (findBlockedTradeReferenceEmailDomain(emailVal, blockedTradeRefDomains)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: blockedTradeReferenceEmailMessage(basicSupplier?.name),
        });
        continue;
      }

      if (personalEmail && emailVal === personalEmail) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: 'Trade reference email cannot be the same as your personal email.',
        });
        continue;
      }

      if (seenTradeEmails.includes(emailVal)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText:
            'Duplicate trade reference email. Each trade reference must have a unique email.',
        });
      } else {
        seenTradeEmails.push(emailVal);
      }
    }

    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  const handleValidateConditionalSelectedField = () => {
    const stepValidateResults = Object.values(stepRefs.current)
      .filter((r): r is ConditionalOptionPreviewRef => !!r)
      .map((r) => r.validate());
    const conditionalOptionFieldsValid = stepValidateResults.every((res) => res !== false);

    const currentStepFields = allStepFieldsValues
      .filter((f) => f.fieldValue && f.fromField !== 'BANK_REFERENCE')
      .flatMap((f) => {
        // f.fieldValue may still contain sub-fields from a previously selected
        // conditional-option branch (e.g. the app was saved with "yes" selected,
        // then the customer switched to "no"). Only validate sub-fields that
        // belong to the currently selected option (f.value), same scoping
        // useConditionalOption.validate() applies via optionStates[selectedOption].
        const selectedOptionSlug = String(f.value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '-');
        const keyPrefix = `${
          f.fromField === 'DYNAMIC_FORM_DOCUMENT' ? 'dynamic-form' : 'conditional-option-select'
        }-${selectedOptionSlug}-`;
        return (f.fieldValue ?? []).filter((sub) =>
          sub?.uniqueId?.toLowerCase().startsWith(keyPrefix)
        );
      });

    const conditionalFieldErrors: { uniqueId: string; errorText: string }[] = [];

    currentStepFields.forEach((field) => {
      if (!field) return;
      if (field.hidden) return;

      if (
        field.isRequired &&
        ((typeof field.value === 'string' && field.value.trim() === '') || field.value === null)
      ) {
        conditionalFieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} is required.`,
        });
        return;
      }

      if (
        field.isFieldEmail &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(field.value)) {
          conditionalFieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} must be a valid email.`,
          });
        }
      }
    });

    setErrors((prev) => [
      ...prev.filter((e) => !currentStepFields.some((f) => f?.uniqueId === e.uniqueId)),
      ...conditionalFieldErrors,
    ]);

    if (conditionalFieldErrors.length > 0) {
      openDillToast({
        type: 'ERROR',
        message: `Please complete: ${conditionalFieldErrors.map((e) => e.errorText).join(' ')}`,
      });
    }

    return conditionalOptionFieldsValid && conditionalFieldErrors.length === 0;
  };

  const handleUpdateInviteSigners = (
    data: {
      title: string;
      email: string;
      step: number;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      minimumNumberOfSignatures?: number;
    }[]
  ) => {
    setInvitedSignatures(data);
    setInvitedSignaturesErrors(
      data.map((item) => ({ placeHolder: item?.placeHolder ?? '', errorText: '' }))
    );
  };

  const isInviteFieldVisible = useCallback(
    (sig: StateInvitedUser): boolean => {
      if (!sig.id) return true;
      const inviteField = allStepFieldsValues.find((f) => f.id === sig.id);
      if (!inviteField) return false;
      if (!(inviteField as any)?.conditionalVisibility?.sourceFieldId) return true;
      const { sourceFieldId, sourceOptionValues } = (inviteField as any).conditionalVisibility;
      const sourceValue = String(
        allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
      );
      return (sourceOptionValues ?? []).includes(sourceValue);
    },
    [allStepFieldsValues]
  );

  const isNecessarySignatures = useCallback(
    (sig: StateInvitedUser): boolean => {
      if (!sig.id) return true;
      const inviteField = allStepFieldsValues.find((f) => f.id === sig.id);
      if (!inviteField) return true;
      if (!(inviteField as any)?.conditionalVisibility?.sourceFieldId) return true;
      const { sourceFieldId, sourceOptionValues } = (inviteField as any).conditionalVisibility;
      const sourceValue = String(
        allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
      );
      return (sourceOptionValues ?? []).includes(sourceValue);
    },
    [allStepFieldsValues]
  );

  const signatureDetails = useMemo(() => {
    const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;
    const email = (emailField as string) || userEmail || '';
    const updatedSignature = invitedSignatures.filter(isInviteFieldVisible).map((item) => ({
      title: item.title,
      email: item.email,
      fields: item.fields || [],
      signature: item.signature || '',
      placeHolder: item.placeHolder || '',
      type: item.type || ('AUTHORIZED_AGENT' as const),
      ...(item.dateSigned ? { dateSigned: item.dateSigned } : {}),
      ...(item.ipAddress ? { ipAddress: item.ipAddress } : {}),
      ...(item.location ? { location: item.location } : {}),
      ...(item.minimumCreditAmount ? { minimumCreditAmount: item.minimumCreditAmount } : {}),
      ...(item.minimumNumberOfSignatures !== undefined
        ? {
            minimumNumberOfSignatures: item.minimumNumberOfSignatures,
          }
        : {}),
      ...(item?.linkedFieldId ? { linkedFieldId: item.linkedFieldId } : {}),
      ...(item.signaturePageFieldIds ? { signaturePageFieldIds: item.signaturePageFieldIds } : {}),
    }));

    return { email, invitedSignatures: updatedSignature };
  }, [allStepFieldsValues, invitedSignatures, userEmail, isInviteFieldVisible]);

  const necessarySignatureDetails = useMemo(() => {
    const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;
    const email = (emailField as string) || userEmail || '';
    const updatedSignature = invitedSignatures.filter(isNecessarySignatures).map((item) => ({
      title: item.title,
      email: item.email,
      fields: item.fields || [],
      signature: item.signature || '',
      placeHolder: item.placeHolder || '',
      type: item.type || ('AUTHORIZED_AGENT' as const),
      ...(item.dateSigned ? { dateSigned: item.dateSigned } : {}),
      ...(item.ipAddress ? { ipAddress: item.ipAddress } : {}),
      ...(item.location ? { location: item.location } : {}),
      ...(item.minimumCreditAmount ? { minimumCreditAmount: item.minimumCreditAmount } : {}),
      ...(item.minimumNumberOfSignatures !== undefined
        ? {
            minimumNumberOfSignatures: item.minimumNumberOfSignatures,
          }
        : {}),
      ...(item?.linkedFieldId ? { linkedFieldId: item.linkedFieldId } : {}),
      ...(item.signaturePageFieldIds ? { signaturePageFieldIds: item.signaturePageFieldIds } : {}),
    }));

    return { email, invitedSignatures: updatedSignature };
  }, [allStepFieldsValues, invitedSignatures, userEmail, isNecessarySignatures]);

  // `signatureDetails` only holds slots that are currently visible/filled in. `necessarySignatureDetails`
  // is the full set of required slots (e.g. a corporate guarantor whose terms still need to reach the
  // backend even if that section isn't currently rendered). Combine by `placeHolder`: keep everything
  // already in `signatureDetails` as-is, and append whichever necessary slots are missing from it.
  const combineSignatureDetails = (
    filled: typeof signatureDetails,
    necessary: typeof necessarySignatureDetails
  ): typeof signatureDetails => {
    const filledPlaceHolders = new Set(filled.invitedSignatures.map((item) => item.placeHolder));
    const missingSignatures = necessary.invitedSignatures.filter(
      (item) => !filledPlaceHolders.has(item.placeHolder)
    );

    return {
      email: filled.email || necessary.email,
      invitedSignatures: [...filled.invitedSignatures, ...missingSignatures],
    };
  };

  const processData = async (fieldsOverride?: typeof allStepFieldsValues) => {
    try {
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;
      const fieldsToSubmit = fieldsOverride ?? allStepFieldsValues;

      const isFieldCurrentlyVisible = (f: (typeof fieldsToSubmit)[number]): boolean => {
        const conditionalVisibility = (f as any)?.conditionalVisibility as
          | { sourceFieldId: string; sourceOptionValues: string[] }
          | null
          | undefined;
        if (!conditionalVisibility?.sourceFieldId) return true;
        const sourceValue = String(
          fieldsToSubmit.find((sf) => sf.id === conditionalVisibility.sourceFieldId)?.value ?? ''
        );
        return (conditionalVisibility.sourceOptionValues ?? []).includes(sourceValue);
      };

      const clearedFieldsToSubmit = fieldsToSubmit.map((f) =>
        isFieldCurrentlyVisible(f) ? f : { ...f, value: getDefaultValue(f) }
      );

      const fieldValues = await Promise.all(
        clearedFieldsToSubmit.map(async (f) => {
          let value = f.value;
          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            try {
              const uploadFileUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath: `creditApplicationsFiles/${
                  creditApplication?.supplierId || ''
                }/${Date.now()}`,
              });
              if (uploadFileUrl) {
                value = {
                  name: value.name,
                  url: uploadFileUrl ?? '',
                  fileType: fileType,
                };
              } else {
                throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
              }
            } catch (error) {
              Sentry.captureException(error);
              console.error(`❌ Failed to upload ${f.name}: "${value.name}":`, error);
              throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
            }
          }

          if (f.isHashed) {
            if (secretKey) {
              const token = CryptoJS.AES.encrypt(JSON.stringify(f.value), secretKey).toString();
              value = token;
            }
          }
          if (f.fromField === 'DATE' && f.value) {
            const formattedDate = moment(f.value).format('MM-DD-YYYY');
            value = formattedDate;
          }
          const fieldData: ConditionalOptionValues[] = [];
          if (f.fieldValue) {
            const fieldValue = await Promise.all(
              f.fieldValue.map(async (fv) => {
                let fvvalue = fv.value;
                if (fvvalue instanceof File) {
                  const fileType = fvvalue.name.split('.').pop() || '';
                  try {
                    const uploadFileUrl = await firebaseLargeFileUploadHelper({
                      blobUrl: URL.createObjectURL(fvvalue),
                      fileNameWithExtension: fvvalue.name,
                      storagePath: `creditApplicationFiles/${
                        creditApplication?.supplierId || ''
                      }/${Date.now()}`,
                    });
                    if (uploadFileUrl) {
                      fvvalue = {
                        name: fvvalue.name,
                        url: uploadFileUrl ?? '',
                        fileType: fileType,
                      };
                    } else {
                      throw new Error(
                        `Upload failed for ${f.name}: "${value.name}". Please try again.`
                      );
                    }
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error(`❌ Failed to upload ${f.name}: "${fvvalue.name}":`, error);
                    throw new Error(
                      `Upload failed for ${f.name}: "${fvvalue.name}". Please try again.`
                    );
                  }
                }

                if (fv.isHashed) {
                  if (secretKey) {
                    const token = CryptoJS.AES.encrypt(
                      JSON.stringify(fv.value),
                      secretKey
                    ).toString();
                    fvvalue = token;
                  }
                }
                if (fv.fromField === 'DATE' && fv.value) {
                  const formattedDate = moment(fv.value).format('MM-DD-YYYY');
                  fvvalue = formattedDate;
                }
                return {
                  ...fv,
                  value: fvvalue,
                };
              })
            );
            fieldData.push(...fieldValue);
          }
          return {
            uniqueId: f.uniqueId,
            id: f.id,
            name: f.name,
            fromField: f.fromField,
            isRequired: f.isRequired,
            isHashed: f.isHashed,
            stepName: f.stepName,
            stepNumber: f.stepNumber,
            groupStepPosition: f.groupStepPosition || 0,
            groupedPosition: f.groupedPosition || 0,
            groupName: f.groupName || '',
            groupColumnType: f.groupColumnType || ('ONE_COLUMN' as const),
            isFieldArray: f.isFieldArray || false,
            index: f.index || 0,
            isFieldEmail: f.isFieldEmail || false,
            value,
            ...(f.optionType ? { optionType: f.optionType } : {}),
            ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
            ...(f.secondaryValue ? { secondaryValue: f.secondaryValue } : {}),
            ...(f.status ? { status: f.status } : {}),
            ...(f.emailDate ? { emailDate: f.emailDate } : {}),
            ...(f.sendEmail ? { sendEmail: f.sendEmail } : {}),
            ...(f.type ? { type: f.type } : {}),
            ...(f.fieldValue ? { fieldValue: fieldData } : {}),
            ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
            ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
            ...(f.currencyDecimalPlaces !== undefined
              ? { currencyDecimalPlaces: f.currencyDecimalPlaces }
              : {}),
            ...(f.addressVerification ? { addressVerification: f.addressVerification } : {}),
            ...(f.borderId ? { borderId: f.borderId } : {}),
            ...((f as any).formTemplate ? { formTemplate: (f as any).formTemplate } : {}),
            ...(f.conditionalVisibility ? { conditionalVisibility: f.conditionalVisibility } : {}),
            ...(f.signaturePageFieldIds ? { signaturePageFieldIds: f.signaturePageFieldIds } : {}),
          };
        })
      );

      let hashedBankReference = bankReference;
      if (secretKey && bankReference && bankReference.length > 0) {
        hashedBankReference = bankReference.map((bankRef) => {
          const token = CryptoJS.AES.encrypt(
            JSON.stringify(bankRef.bankDetails.accountNumber),
            secretKey
          ).toString();

          return {
            ...bankRef,
            bankDetails: {
              ...bankRef.bankDetails,
              accountNumber: token,
            },
          };
        });
      }

      const combinedSignatureDetails = combineSignatureDetails(
        signatureDetails,
        necessarySignatureDetails
      );

      // Signature fields hold decrypted values while editing, so hashed ones (e.g. guarantor SSN)
      // must be re-encrypted here. Values still in ciphertext were never decrypted — leave as-is.
      const hashedInvitedSignatures = combinedSignatureDetails.invitedSignatures.map((sig) => ({
        ...sig,
        fields: (sig.fields ?? []).map((f: any) =>
          f.isHashed &&
          secretKey &&
          f.value !== null &&
          f.value !== undefined &&
          f.value !== '' &&
          !(typeof f.value === 'string' && f.value.startsWith('U2FsdGVkX1'))
            ? { ...f, value: CryptoJS.AES.encrypt(JSON.stringify(f.value), secretKey).toString() }
            : f
        ),
      }));

      const data = {
        fieldValues,
        invitedSignatures: hashedInvitedSignatures,
        ...(hashedBankReference ? { bankReference: hashedBankReference } : {}),
      };
      return { data, message: '' };
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return {
        data: null,
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      };
    }
  };

  // Same rule as trade reference emails: a manual bank reference's contact can't be at the
  // supplier or at the applicant's own company — see getBlockedTradeReferenceEmailDomains.
  const blockedBankReferenceEmailDomains = getBlockedTradeReferenceEmailDomains(basicSupplier, [
    userEmail,
    getSourceApplication()?.senderEmail,
    getSourceApplication()
      ?.fieldValues.find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase(),
  ]);

  const validateBankReferences = () => {
    const currentStepFields = allStepFieldsValues;
    const bankReferenceField = currentStepFields.find(
      (f) => f.fromField === 'BANK_REFERENCE' && f.isRequired
    );
    if (
      currentStepFields.some((f) => f.fromField === 'BANK_REFERENCE') &&
      findBlockedBankReferenceEmail(bankReference, blockedBankReferenceEmailDomains)
    ) {
      setBankError(blockedBankReferenceEmailMessage(basicSupplier?.name));
      return false;
    }
    if (bankReferenceField) {
      if (!bankReference) {
        setBankError('Bank reference is required.');
        return false;
      }
      setBankError(null);
      return true;
    }
    setBankError(null);
    return true;
  };

  const validateInviteSigners = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailPersonal =
      (allStepFieldsValues.find((f) => f.id === 'email-personal')?.value as string) || userEmail;
    const senderEmail = (emailPersonal ?? '').trim().toLowerCase();

    let foundInvitedSig = invitedSignatures.filter(isInviteFieldVisible);

    const bankRefField = allStepFieldsValues.find((f) => f.fromField === 'BANK_REFERENCE');
    if (
      bankRefField &&
      bankRefField.showIfNotPlaid &&
      bankRefField.linkedFieldId &&
      bankReference?.length
    ) {
      const manualBankRef = bankReference.find((f) => f.connectionType === 'manual');
      if (!manualBankRef) {
        const linkedId = bankRefField.linkedFieldId;
        foundInvitedSig = foundInvitedSig.filter((s) => s?.id && !linkedId.includes(s.id));
      }
    }

    const foundMinimumCreditAmount =
      foundInvitedSig.find(
        (item) =>
          (item.minimumCreditAmount && item.minimumCreditAmount > 0 && item.type === 'GUARANTOR') ||
          item.type === 'CORPORATE_GUARANTOR'
      )?.minimumCreditAmount ?? 0;

    const foundAmount = allStepFieldsValues.find((f) => f.id.includes('amount-of'))?.value ?? 0;

    const validateEmail = (email: string, placeHolder: string, customMessage?: string) => {
      if (!email || email.trim() === '') {
        return customMessage || `${placeHolder} email is required.`;
      }
      if (!emailRegex.test(email)) {
        return `${placeHolder} email is not valid.`;
      }
      return '';
    };

    const validateByType = (item: StateInvitedUser) => {
      // Sign Now: if the slot email matches the sender's own email and hasn't been signed yet
      if (
        senderEmail &&
        item.email?.trim().toLowerCase() === senderEmail &&
        !item.signature &&
        !signedNowPlaceholders.includes(item.placeHolder ?? '')
      ) {
        return 'This is your email — please use "Sign Now" to sign this slot.';
      }

      const groupFilter = (s: StateInvitedUser) =>
        s.type === item.type &&
        (item.signatureGroupName ? s.signatureGroupName === item.signatureGroupName : true);
      const signers = foundInvitedSig.filter((s) => groupFilter(s) && s.email !== '');
      const invited = foundInvitedSig.filter(groupFilter);

      const min = item.minimumNumberOfSignatures;
      const hasMinDefined = min !== undefined;

      const requiresAmountCheck =
        item.type === 'GUARANTOR' ||
        (item.type === 'CORPORATE_GUARANTOR' && foundAmount > foundMinimumCreditAmount);

      let errorText = '';

      if (hasMinDefined && min > signers.length && min !== invited.length) {
        errorText = validateEmail(
          item.email,
          item?.placeHolder ?? '',
          `At least ${min} ${item.type.toLowerCase().replace('_', ' ')} email is required.`
        );
      } else if (hasMinDefined && min > signers.length && min === invited.length) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (!hasMinDefined && requiresAmountCheck) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (item.isRequired && (!item.email || item.email.trim() === '')) {
        errorText = `${item?.placeHolder ?? ''} email is required.`;
      } else if (item.email?.trim() !== '' && !emailRegex.test(item.email)) {
        errorText = `${item?.placeHolder ?? ''} email is not valid.`;
      }
      return errorText;
    };

    const inviteErrors = foundInvitedSig.map((item) => ({
      placeHolder: item?.placeHolder ?? '',
      errorText: validateByType(item),
    }));

    const typeMap: Record<string, Map<string, StateInvitedUser[]>> = {};
    const uniqueErrors: { placeHolder: string; errorText: string }[] = [];

    foundInvitedSig
      .filter((item) => item.email?.trim() !== '')
      .forEach((item) => {
        if (!typeMap[item.type]) {
          typeMap[item.type] = new Map();
        }
        const emailMap = typeMap[item.type];
        if (!emailMap.has(item.email.toLowerCase())) {
          emailMap.set(item.email.toLowerCase(), []);
        }
        emailMap.get(item.email.toLowerCase())?.push(item);
      });

    Object.values(typeMap).forEach((emailMap) => {
      emailMap.forEach((items) => {
        if (items.length > 1) {
          items.forEach((item) => {
            uniqueErrors.push({
              placeHolder: item.placeHolder ?? '',
              errorText: 'Email should not be the same.',
            });
          });
        }
      });
    });

    const mergedErrors = inviteErrors.map((err) => {
      const uniqueError = uniqueErrors.find((u) => u.placeHolder === err.placeHolder);
      return uniqueError && !err.errorText ? { ...err, errorText: uniqueError.errorText } : err;
    });

    setInvitedSignaturesErrors(mergedErrors);
    return mergedErrors.every((error) => error.errorText === '');
  };

  const handleSubmit = async () => {
    if (!creditApplication) return;

    if (!handleValidateConditionalSelectedField()) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!validateInviteSigners()) return;

    if (!validateBankReferences()) return;

    setIsLoading(true);
    try {
      const verifiedFields = await verifyAddressFieldsOnSubmit(allStepFieldsValues);
      setAllStepFieldsValues(verifiedFields);

      const data = await processData(verifiedFields);
      if (!data.data) {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: 'Error submitting application',
            subTitle: data.message || '',
            onClose: () => {},
          })
        );
        return;
      }

      const sourceApp = getSourceApplication();
      const sourceFieldValues = sourceApp?.fieldValues ?? creditApplication.fieldValues ?? [];

      const existingUniqueIds = new Set(sourceFieldValues.map((f) => f.uniqueId));

      const fieldValues = [...sourceFieldValues].map((field) => {
        const fieldValue = (data?.data?.fieldValues ?? []).find(
          (f) => f.uniqueId === field.uniqueId
        );
        if (fieldValue) {
          return fieldValue;
        }
        return field;
      });

      // Append new fields added via "Add Group" that don't exist in the saved application yet
      const newFields = (data?.data?.fieldValues ?? []).filter(
        (f) => !existingUniqueIds.has(f.uniqueId)
      );
      const finalFieldValues = [...fieldValues, ...newFields];

      const oldCompanyName = sourceFieldValues.find(
        (f) => f.id === 'customer-or-company-name'
      )?.value;
      const newCompanyName = finalFieldValues.find(
        (f) => f.id === 'customer-or-company-name'
      )?.value;
      const preserveSignatures = newCompanyName === oldCompanyName;

      const response = applicationHistoryId
        ? await dispatch(
            updateApplicationUpdateHistory({
              creditApplicationId: creditApplicationId,
              applicationHistoryId: applicationHistoryId,
              fieldValues: finalFieldValues,
              supplierId: creditApplication?.supplierId || '',
              savingType: 'update signature',
              preserveSignatures,
              isFeedbackResponse: updateType !== 'update',
              ...(data?.data?.invitedSignatures && {
                invitedSignatures: data?.data?.invitedSignatures,
              }),
            })
          )
        : await dispatch(
            updateCreditApplication({
              creditApplicationId: creditApplicationId,
              fieldValues: finalFieldValues,
              ...(data?.data?.invitedSignatures && {
                invitedSignatures: data?.data?.invitedSignatures,
              }),
              savingType: 'update signature',
              preserveSignatures,
              isFeedbackResponse: updateType !== 'update',
            })
          );

      if (response.type.includes('fulfilled')) {
        setIsSaved(true);
        setModalState(ModalState.SUCCESS_UPDATING_FIELDS);
        setModalDetails({
          modalType: 'Success',
          title: updateType === 'update' ? `${applicationTitle} Update` : 'Field Updated',
          details: `Your fields have been successfully updated in the ${applicationTitle.toLowerCase()}.`,
        });
      } else {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: `Error Updating ${applicationTitle}`,
            subTitle: `There was an error updating the ${applicationTitle.toLowerCase()}. Please try again later.`,
            onClose: () => {},
          })
        );
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddGroupedField = ({
    groupName,
    position,
    step,
  }: {
    groupName: string;
    position: number;
    step: CreditApplicationStep;
  }) => {
    const currentStepFields = step?.fields || [];

    const groupedFields =
      currentStepFields.find((f) => f.groupName === groupName && f.position === position)
        ?.groupedFields || [];

    if (groupedFields.length === 0) return;

    const newGroupedFields = duplicateWithNextGroupedPosition(groupedFields);

    setFetchedCreditApplicationTemplate((prev) => {
      if (!prev) return [];

      return prev.map((st) =>
        st.name === step.name
          ? {
              ...st,
              fields: st.fields.map((f) =>
                f.groupName === groupName && f.position === position
                  ? { ...f, groupedFields: newGroupedFields }
                  : f
              ),
            }
          : st
      );
    });
  };

  const handleRemoveGroupedField = ({
    groupName,
    position,
    groupPostion,
    step,
  }: {
    groupName: string;
    position: number;
    groupPostion: number;
    step: CreditApplicationStep;
  }) => {
    if (!fetchedCreditApplicationTemplate) return;

    const currentStepFields = step.fields || [];

    const groupedFields =
      currentStepFields.find((f) => f.groupName === groupName && f.position === position)
        ?.groupedFields || [];

    if (groupedFields.length === 0) return;

    const newGroupedFields = groupedFields.filter((f) => f.groupedPosition !== groupPostion);

    setFetchedCreditApplicationTemplate((prev) => {
      if (!prev) return [];

      return prev.map((st) =>
        st.name === step.name
          ? {
              ...st,
              fields: st.fields.map((f) =>
                f.groupName === groupName && f.position === position
                  ? { ...f, groupedFields: newGroupedFields }
                  : f
              ),
            }
          : st
      );
    });
  };

  const handleBankReferenceChange = (
    updater: BankReference[] | null | ((prev: BankReference[] | null) => BankReference[] | null)
  ) => {
    setBankReference((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const signNowInvitedSignatures = useMemo(() => {
    if (!signNowFieldData) return [];
    return [
      {
        placeHolder: signNowFieldData.placeHolder ?? '',
        email: signatureDetails.email,
        fields: signNowFieldData.fields,
        signature: '',
        type: signNowFieldData.type,
      },
    ];
  }, [signNowFieldData, signatureDetails.email]);

  const handleSignNow = (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => {
    setSignature('');
    setSignNowFieldData({
      ...field,
      fields: field.fields.map((f) => ({ ...f, value: '' })),
    });
    setOpenSignNowModal(true);
  };

  const handleSignNowSubmit = (data: {
    dynamicFields: DynamicInvitedSignaturesFields[];
    signature: string;
  }) => {
    if (!signNowFieldData) return;
    setInvitedSignatures((prev) =>
      prev.map((sig) => {
        if (sig.placeHolder !== signNowFieldData.placeHolder) return sig;
        const mergedFields = sig.fields.map((f) => {
          const match = data.dynamicFields.find((pf) => f.id.includes(pf.id));
          return match ? { ...f, value: match.value } : f;
        });
        return {
          ...sig,
          email: signatureDetails.email,
          fields: mergedFields,
          signature: data.signature,
          dateSigned: moment().format('MM-DD-YYYY'),
          ipAddress: ipdData?.ipAddress ?? '',
          location: ipdData?.location ?? '',
        };
      })
    );
    setSignedNowPlaceholders((prev) => [...prev, signNowFieldData.placeHolder ?? '']);
    setOpenSignNowModal(false);
  };

  const handleEditSignNow = (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => {
    setSignature('');
    setSignNowFieldData(field);
    setOpenSignNowModal(true);
  };

  const handleClearSignNow = (field: {
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  }) => {
    setSignedNowPlaceholders((prev) => prev.filter((p) => p !== field.placeHolder));
    setInvitedSignatures((prev) =>
      prev.map((sig) => {
        if (sig.placeHolder !== field.placeHolder) return sig;
        return {
          ...sig,
          email: '',
          signature: '',
          fields: sig.fields.map((f) => ({ ...f, value: '' })),
        };
      })
    );
  };

  return (
    <div className="w-full GREY_100-BG min-h-full pb-4">
      {/* Header */}
      <div className="w-full flex items-center justify-center text-lg sm:text-xl WHITE-BG font-bold p-3 sm:p-5 text-center">
        {`Review And Update ${applicationTitle}`}
      </div>

      {/* Content */}
      {modalState === ModalState.VERIFY_LOGIN && (
        <div className="flex flex-col px-3 sm:px-5 py-4 w-full justify-center items-center">
          <div className="w-full md:w-1/3 mt-3 md:mt-6 WHITE-BG rounded-lg p-2 md:p-6">
            {domainIdUrl && (
              <div className="flex flex-col items-center mb-6 md:mb-14">
                <img className="max-h-14 md:max-h-20 object-contain" src={domainIdUrl} />
                <div className="flex items-center mt-2">
                  <p className="text-xs md:text-sm">Powered By</p>
                  <a href="https://usedill.com/" target="_blank">
                    <img className="h-8 md:h-12 ml-2" src={dillLogoWithText} />
                  </a>
                </div>
              </div>
            )}
            {!domainIdUrl && (
              <div className="flex flex-col items-center mb-14 w-full">
                <img className="w-8/12" src={dillLogoWithText} />
              </div>
            )}

            <div className="flex flex-col bg-white rounded-lg p-6 shadow-md">
              <p className="text-center font-bold text-sm">
                Check your email for a verification link to sign in to your portal. *Make sure to
                open the link from this browser.
              </p>
              <p className="text-center text-sm mt-6">
                Didn’t receive the link?{' '}
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer"
                  onClick={() => handleCheckLogin(true)}>
                  Resend here
                </span>
              </p>
              <p className="text-center text-sm mt-6">
                If you're still not receiving the link, check with your IT department to whitelist
                the domain <span className="PRIMARY_500-CLR ">usedill.com</span>
              </p>
              <p className="text-2xs text-center">
                Current email: <span className="PRIMARY_500-CLR ">{userEmail}</span>
              </p>
            </div>
          </div>
        </div>
      )}
      {modalState === ModalState.UPDATE_FIELDS && (
        <div>
          {creditApplication && (
            <div className="flex flex-col px-3 sm:px-5 py-4 w-full justify-center items-center">
              <div className="w-full md:w-2/3 bg-white p-3 sm:p-4 gap-4 rounded-md shadow-sm">
                <p className="mb-2 GREY_500-CLR text-sm mb-1">
                  Updating{' '}
                  <strong>{`${
                    allStepFieldsValues.find((f) => f.id === 'customer-or-company-name')?.name ||
                    'Customer or Company Name'
                  }`}</strong>{' '}
                  field, if signatures required, will require new signatures. Signature emails will
                  be sent after you review and update below. Updating any other field will not
                  require new signatures.
                </p>
                {fetchedCreditApplicationTemplate &&
                  fetchedCreditApplicationTemplate.length > 0 && (
                    <div>
                      <div className="flex flex-col gap-4 mb-4">
                        {fetchedCreditApplicationTemplate.map((step, i) => {
                          return (
                            <div
                              key={i + 'feedback-submit'}
                              className={`flex w-full border justify-center flex-col border-gray-200 rounded-xl shadow-sm overflow-hidden`}>
                              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                <h2 className="text-sm font-bold text-gray-800 tracking-wide">
                                  {step.name}
                                </h2>
                              </div>
                              <div className="w-full px-4 pt-4">
                                <DynamicStepForm
                                  ref={(el) => {
                                    stepRefs.current[i] = el;
                                  }}
                                  title={step.name}
                                  stepFields={step.fields || []}
                                  formData={allStepFieldsValues}
                                  onChange={onChangeFormData}
                                  errors={errors}
                                  domainDetails={domainDetails}
                                  onAddGroupedField={({
                                    groupName,
                                    position,
                                  }: {
                                    groupName: string;
                                    position: number;
                                  }) =>
                                    handleAddGroupedField({
                                      groupName,
                                      position,
                                      step,
                                    })
                                  }
                                  onRemoveGroupedField={({
                                    groupName,
                                    position,
                                    groupPostion,
                                  }: {
                                    groupName: string;
                                    position: number;
                                    groupPostion: number;
                                  }) =>
                                    handleRemoveGroupedField({
                                      groupName,
                                      position,
                                      groupPostion,
                                      step,
                                    })
                                  }
                                  onUpdateInviteSigners={handleUpdateInviteSigners}
                                  invitedSignatures={invitedSignatures}
                                  invitedSignaturesErrors={invitedSignaturesErrors}
                                  bankReference={bankReference}
                                  onUpdateBankReference={handleBankReferenceChange}
                                  blockedBankReferenceEmailDomains={
                                    blockedBankReferenceEmailDomains
                                  }
                                  bankError={bankError}
                                  bankReferenceRequiredFields={
                                    creditApplicationTemplate?.bankReferenceRequiredFields || []
                                  }
                                  amountOfCredit={0}
                                  showStepName={false}
                                  onSignNow={handleSignNow}
                                  onEditSignNow={handleEditSignNow}
                                  onClearSignNow={handleClearSignNow}
                                  signedNowPlaceholders={signedNowPlaceholders}
                                  senderEmail={signatureDetails.email}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                <div className="mb-2 GREY_500-CLR text-xs">
                  By continuing, you agree to the{' '}
                  <a
                    href="https://www.usedill.com/terms"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.usedill.com/privacy"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Privacy Policy
                  </a>
                </div>
                {invitedSignaturesErrors.some((e) => e.errorText !== '') && (
                  <p className="text-red-500 text-sm text-center mt-2">
                    Please review the signer errors above before submitting.
                  </p>
                )}
                <div className="flex w-full justify-center items-center gap-2 mt-4">
                  <AppButton
                    text="Submit"
                    type="PRIMARY"
                    buttonStyles={{ width: '150px', height: '40px' }}
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    isDisabled={isLoading || !hasFormChanges}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {modalState === ModalState.SUCCESS_UPDATING_FIELDS && (
        <div className="flex p-4 w-full justify-center items-center">
          <JobSavingSuccess
            type={modalDetails.modalType}
            details={modalDetails.details}
            title={modalDetails.title}
          />
        </div>
      )}
      <CreateSignatureModal
        open={signatureModalOpen}
        handleClose={() => setSignatureModalOpen(false)}
        addSignature={(sign) => {
          setSignature(sign);
          setSignatureModalOpen(false);
        }}
        defaultIsManualSign={false}
        onDownloadRealease={() => {}}
        subText="Please sign the credit application to proceed."
        disableTypedSignature={basicSupplier?.isCreditAppTypedSignatureDisabled ?? false}
      />
      <CustomerSignatureModal
        open={openSignNowModal}
        handleClose={() => setOpenSignNowModal(false)}
        email={signatureDetails.email}
        invitedSignature={signNowInvitedSignatures}
        signature={signature}
        onOpenSignature={() => setSignatureModalOpen(true)}
        onSignCreditApplication={handleSignNowSubmit}
      />
    </div>
  );
};
